import type { PageStore } from "@/store/PageStore";
import { AppStore } from "@/store";

import { loadOrganizableNotes } from "@/actions/autoOrganizeNotes";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { INoteObservable } from "@/store/note";
import { toastModule } from "@/modules/toast";

const loadOrganizableCollections = async ({
  collectionIds,
  store,
}: {
  collectionIds: string[];
  store: AppStore;
}): Promise<CollectionObservable[] | null> => {
  const collections: CollectionObservable[] = [];

  for (const collectionId of collectionIds) {
    const collection = await store.collections.getAsync(collectionId);

    if (collection) {
      collections.push(collection);
    }
  }

  if (collections.length === 0) {
    toastModule.triggerToast({
      content: <div>Unable to find selected collections.</div>,
    });

    return null;
  }

  return collections;
};

interface StartChatWithEntitiesArgs {
  noteIds?: string[];
  collectionIds?: string[];
  store: AppStore;
  pageStore?: PageStore;
  overrideContext?: boolean;
}

export const startChatWithEntities = async ({
  noteIds,
  collectionIds,
  store,
  pageStore,
  overrideContext = false,
}: StartChatWithEntitiesArgs): Promise<void> => {
  const uniqueNoteIds = noteIds ? Array.from(new Set(noteIds)) : [];
  const uniqueCollectionIds = collectionIds ? Array.from(new Set(collectionIds)) : [];

  let notes: INoteObservable[] = [];
  let collections: CollectionObservable[] = [];

  if (uniqueNoteIds.length > 0) {
    const loadedNotes = await loadOrganizableNotes({ noteIds: uniqueNoteIds, store });
    if (loadedNotes) {
      notes = loadedNotes;
    }
  }

  if (uniqueCollectionIds.length > 0) {
    const loadedCollections = await loadOrganizableCollections({
      collectionIds: uniqueCollectionIds,
      store,
    });
    if (loadedCollections) {
      collections = loadedCollections;
    }
  }

  const hasEntities = notes.length > 0 || collections.length > 0;

  if (!hasEntities) {
    return;
  }

  const resolvedPageStore = pageStore ?? store.pageStore;
  const memItPage = resolvedPageStore?.memItPage;

  const sessionId = await store.sessions.getLatestInitialChatSession();

  if (memItPage) {
    if (overrideContext) {
      memItPage.clearAllMentions();
    }

    notes.forEach(note => memItPage.addUserNoteMention(note));
    collections.forEach(collection => memItPage.addUserCollectionMention(collection));
    memItPage.handleSelectSession(sessionId);
  } else {
    store.sidePanel.goToMemIt(sessionId);
  }
};
